import React, { useEffect, useRef, useState } from 'react';
import { MEMBER_STUDENT_NUMBER_MAX, MEMBER_STUDENT_NUMBER_MIN } from '../../utils/classMemberNumbers';
import './ClassMemberOrderNumberInput.css';

/** 고정 학생 번호 입력값 파싱: 1~9999 정수만 허용(중간 빈 번호 허용, 인원 수와 무관). */
export function parseMemberStudentNumberInput(raw: string): number | null {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  if (!Number.isSafeInteger(n) || n < MEMBER_STUDENT_NUMBER_MIN || n > MEMBER_STUDENT_NUMBER_MAX) return null;
  return n;
}

/**
 * 포커스 직후(아직 아무것도 고치지 않은 상태)의 첫 입력은 기존 번호를 "대체"한다.
 * 브라우저가 클릭 시 전체 선택을 풀어 버려 "3" 뒤에 "1"이 붙어 "31"이 되는 문제를 막는다.
 */
export function nextOrderDraftOnChange(
  rawValue: string,
  previous: string,
  insertedData: string | null | undefined,
  pristine: boolean
): string {
  const digits = rawValue.replace(/[^0-9]/g, '');
  if (!pristine || digits.length <= previous.length) return digits;
  const typed = (insertedData ?? '').replace(/[^0-9]/g, '');
  if (typed) return typed;
  if (previous && digits.startsWith(previous)) return digits.slice(previous.length);
  if (previous && digits.endsWith(previous)) return digits.slice(0, digits.length - previous.length);
  return digits;
}

type Props = {
  /** 현재 표시 번호 */
  value: number;
  /** 같은 그룹 안에서 번호가 겹치는지 */
  duplicate?: boolean;
  disabled?: boolean;
  /** 새 번호 확정(Enter/포커스 아웃) */
  onCommit: (studentNumber: number) => void;
};

/**
 * 멤버 관리 표의 번호 셀. 번호는 멤버별 "고정 번호"라 순서를 밀지 않고 그대로 저장한다
 * (예: 1~17번 중 17번을 18번으로 바꾸면 17번은 빈 번호로 남는다). 목록은 번호순으로 정렬된다.
 */
const ClassMemberOrderNumberInput: React.FC<Props> = ({ value, duplicate, disabled, onCommit }) => {
  const current = String(value);
  const [draft, setDraft] = useState(current);
  const [invalid, setInvalid] = useState(false);
  const [moved, setMoved] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const focusedRef = useRef(false);
  const pristineRef = useRef(true);
  const justFocusedRef = useRef(false);
  const skipCommitRef = useRef(false);
  const pendingMoveRef = useRef(false);
  const invalidTimerRef = useRef<number | null>(null);
  const movedTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!focusedRef.current) setDraft(current);
    if (pendingMoveRef.current) {
      pendingMoveRef.current = false;
      // 번호순 정렬로 행이 옮겨져도 놓치지 않도록 따라가서 잠깐 강조한다.
      const el = inputRef.current;
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      setMoved(true);
      if (movedTimerRef.current != null) window.clearTimeout(movedTimerRef.current);
      movedTimerRef.current = window.setTimeout(() => setMoved(false), 1400);
    }
  }, [current]);

  useEffect(
    () => () => {
      if (invalidTimerRef.current != null) window.clearTimeout(invalidTimerRef.current);
      if (movedTimerRef.current != null) window.clearTimeout(movedTimerRef.current);
    },
    []
  );

  const flashInvalid = () => {
    setInvalid(true);
    if (invalidTimerRef.current != null) window.clearTimeout(invalidTimerRef.current);
    invalidTimerRef.current = window.setTimeout(() => setInvalid(false), 2200);
  };

  const commit = () => {
    const n = parseMemberStudentNumberInput(draft);
    if (n == null) {
      setDraft(current);
      if (draft.trim() !== current) flashInvalid();
      return;
    }
    if (n === value) {
      setDraft(current);
      return;
    }
    setDraft(String(n));
    pendingMoveRef.current = true;
    onCommit(n);
  };

  const title = invalid
    ? `${MEMBER_STUDENT_NUMBER_MIN}~${MEMBER_STUDENT_NUMBER_MAX} 사이의 정수만 입력할 수 있어요.`
    : duplicate
      ? '같은 그룹에 같은 번호가 있어요. 번호를 바꿔 주세요.'
      : '번호는 멤버마다 고정돼요. 중간에 빈 번호가 있어도 괜찮아요.';

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      className={[
        'cp-class-members-input',
        'cp-class-members-order-input',
        invalid ? 'is-invalid' : '',
        duplicate ? 'is-duplicate' : '',
        moved ? 'is-moved' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      value={draft}
      disabled={disabled}
      maxLength={4}
      title={title}
      aria-label={`멤버 번호 ${value}`}
      aria-invalid={invalid || duplicate || undefined}
      onFocus={(e) => {
        focusedRef.current = true;
        pristineRef.current = true;
        justFocusedRef.current = true;
        pendingMoveRef.current = false;
        e.currentTarget.select();
      }}
      onMouseUp={(e) => {
        // 클릭으로 포커스된 직후의 mouseup이 전체 선택을 풀지 않도록 한다.
        if (justFocusedRef.current) {
          justFocusedRef.current = false;
          e.preventDefault();
          e.currentTarget.select();
        }
      }}
      onChange={(e) => {
        const data = (e.nativeEvent as InputEvent | undefined)?.data;
        const next = nextOrderDraftOnChange(e.target.value, draft, data, pristineRef.current);
        pristineRef.current = false;
        justFocusedRef.current = false;
        setDraft(next);
      }}
      onBlur={() => {
        focusedRef.current = false;
        justFocusedRef.current = false;
        if (skipCommitRef.current) {
          skipCommitRef.current = false;
          setDraft(current);
          return;
        }
        commit();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          skipCommitRef.current = true;
          e.currentTarget.blur();
        }
      }}
    />
  );
};

export default ClassMemberOrderNumberInput;
